/**
 * experimentAutomationBaseline.js
 *
 * WHY THIS EXISTS:
 * Localhost, Dev and production share PostHog project 178803. When someone
 * turns an experiment on, every Playwright spec that does not name an arm is
 * bucketed at random. On 2026-09-24 the challenge and Bootcamp Clarity Home
 * experiments went live at 08:40 UTC and 23, then 58, release tests failed.
 *
 * RULE:
 * On a preview host (localhost or dev.botspot.trade, see experimentOverrideHosts),
 * an automated browser (navigator.webdriver === true) that names no arm gets the
 * experiment's baseline arm. Flag evaluation and exposure tracking are skipped.
 * Production hosts are never held, so real visitors are unaffected.
 *
 * ESCAPE HATCH:
 * A spec that deliberately tests live assignment (usually with a stubbed
 * window.posthog) sets window.__BOTSPOT_ALLOW_LIVE_EXPERIMENT_ASSIGNMENT__
 * before the app loads: `true` for every flag, or an array of flag keys.
 * Use playwright/fixtures/liveExperimentAssignment.ts rather than setting it
 * by hand. Callers with an explicit QA URL can pass
 * trackingData.allowAutomatedLiveAssignment to useABTest instead.
 */
import { isPreviewExperimentOverrideHost } from './experimentOverrideHosts';

export const LIVE_EXPERIMENT_ASSIGNMENT_WINDOW_FLAG =
  '__BOTSPOT_ALLOW_LIVE_EXPERIMENT_ASSIGNMENT__';

// Browser-only module: CRA's build lint rejects globalThis (no-undef),
// and every caller runs in the browser or jsdom.
const isAutomatedBrowser = () => window.navigator?.webdriver === true;

/**
 * True only when a Playwright spec opted this flag in to live assignment with
 * playwright/fixtures/liveExperimentAssignment.ts. Real visitors never set it.
 */
export const hasLiveAssignmentOptIn = (featureFlagKey) => {
  const optIn = window[LIVE_EXPERIMENT_ASSIGNMENT_WINDOW_FLAG];
  if (optIn === true) return true;
  return Array.isArray(optIn) && optIn.includes(featureFlagKey);
};

/**
 * True when this browser must keep the experiment's baseline arm instead of a
 * live PostHog assignment. Explicit arms (preview params, caller forced
 * variants, settings overrides) are resolved by the caller before this check.
 */
export const shouldHoldExperimentBaseline = (
  featureFlagKey,
  hostname = window.location?.hostname || '',
) =>
  isPreviewExperimentOverrideHost(hostname) &&
  isAutomatedBrowser() &&
  !hasLiveAssignmentOptIn(featureFlagKey);

export default shouldHoldExperimentBaseline;
